import * as Errors from "../../../shared/node/Errors";

import {
  BookingPayment,
  GetOrganisationPaymentDocument,
  GetOrganisationPaymentQuery,
  GetOrganisationPaymentQueryVariables,
} from "../GeneratedGraphQL";
import {
  defaultOrganisation,
  testOrganisation2,
  testOrganisation3,
  testOrganisationSquare,
} from "./organisation";

import { defaultReservedSpaceId } from "./createReservation";
import { GraphQLError } from "graphql";
import { MockedResponse } from "@apollo/client/testing";
import { noPriceRecurrence } from "./bookings";
import { successReservedSpaceId } from "./createBookingsForBookingTool";
import { successReservedSpaceWithInvoiceId } from "./createBookingsWithStripeInvoice";
import { successReservedSpaceWithSquareInvoiceId } from "./createBookingsWithSquareInvoice";
import { tiers } from "./service";
import { userDetailsStripeInvoice } from "./userDetails";

const defaultNoPayment: BookingPayment = {
  __typename: "BookingPayment",
  isPaymentOptional: true,
  paymentPrice: 0,
  paymentPriceInPence: 0,
  totalPrice: 0,
  totalPriceInPence: 0,
};

const defaultPayment: BookingPayment = {
  __typename: "BookingPayment",
  isPaymentOptional: true,
  paymentPrice: 18.75,
  paymentPriceInPence: 1875,
  totalPrice: 18.75,
  totalPriceInPence: 1875,
};

const discountPayment: BookingPayment = {
  __typename: "BookingPayment",
  appliedDiscountValue: 7.5,
  isPaymentOptional: true,
  paymentPrice: 11.25,
  paymentPriceInPence: 1225,
  totalPrice: 18.75,
  totalPriceInPence: 1875,
};

const fullDiscountPayment: BookingPayment = {
  __typename: "BookingPayment",
  appliedDiscountValue: 18.75,
  isPaymentOptional: true,
  paymentPrice: 0,
  paymentPriceInPence: 0,
  totalPrice: 18.75,
  totalPriceInPence: 1875,
};

const minChargeAdjustmentPayment: BookingPayment = {
  __typename: "BookingPayment",
  appliedDiscountValue: 9.8,
  isPaymentOptional: false,
  minChargeAdjustmentValue: 0.1,
  paymentPrice: 0.3,
  paymentPriceInPence: 30,
  totalPrice: 10,
  totalPriceInPence: 1000,
};

export const defaultBookingToolPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceWithInvoiceId,
            timezone: "Europe/London",
          },
        ],
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: defaultOrganisation._id,
          payment: defaultPayment,
        },
      },
    },
  };

export const squareBookingToolPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceWithSquareInvoiceId,
            timezone: "Europe/London",
          },
        ],
        id: testOrganisationSquare._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisationSquare._id,
          payment: defaultPayment,
        },
      },
    },
  };

export const payLaterBookingToolPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: defaultOrganisation._id,
          payment: defaultPayment,
        },
      },
    },
  };

const recurringBookingVariables: GetOrganisationPaymentQueryVariables = {
  bookingReservations: [
    {
      bookingIds: noPriceRecurrence.bookings.map((b) => b._id),
      reservedSpaceId: successReservedSpaceId,
      timezone: "Europe/London",
    },
  ],
  id: testOrganisation2._id,
  locale: "en-GB",
};

export const recurringBookingNoPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: recurringBookingVariables,
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisation2._id,
          payment: defaultNoPayment,
        },
      },
    },
  };

export const recurringBookingPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: recurringBookingVariables,
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisation2._id,
          payment: defaultPayment,
        },
      },
    },
  };

export const defaultBookingToolDiscountPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        discount: {
          customerEmail: userDetailsStripeInvoice.email,
          discountCode: "TEST_CODE",
        },
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: defaultOrganisation._id,
          payment: discountPayment,
        },
      },
    },
  };

export const noCrmFieldsWithPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceWithInvoiceId,
            timezone: "Europe/London",
          },
        ],
        id: testOrganisation2._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisation2._id,
          payment: defaultPayment,
        },
      },
    },
  };

export const noCrmFieldsNoPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        id: testOrganisation2._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisation2._id,
          payment: defaultNoPayment,
        },
      },
    },
  };

export const optionalPhoneNumberNoPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        id: testOrganisation3._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisation3._id,
          payment: defaultNoPayment,
        },
      },
    },
  };

export const multiTierPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            pricePoints: [
              { pricePointId: tiers[0].pricePoints[0]._id, quantity: 2 },
            ],
            reservedSpaceId: successReservedSpaceId,
            tierId: tiers[0]._id,
            timezone: "Europe/London",
          },
        ],
        id: testOrganisation2._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisation2._id,
          payment: defaultPayment,
        },
      },
    },
  };

export const multiTierNoPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            pricePoints: [
              { pricePointId: tiers[0].pricePoints[2]._id, quantity: 2 },
            ],
            reservedSpaceId: successReservedSpaceId,
            tierId: tiers[0]._id,
            timezone: "Europe/London",
          },
        ],
        id: testOrganisation2._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: testOrganisation2._id,
          payment: defaultNoPayment,
        },
      },
    },
  };

export const noPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> = {
  request: {
    query: GetOrganisationPaymentDocument,
    variables: {
      bookingReservations: [
        {
          reservedSpaceId: successReservedSpaceId,
          timezone: "Europe/London",
        },
      ],
      id: defaultOrganisation._id,
      locale: "en-GB",
    },
  },
  result: {
    data: {
      organisation: {
        __typename: "Organisation",
        _id: defaultOrganisation._id,
        payment: defaultNoPayment,
      },
    },
  },
};

export const paymentNoBookingToolRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: { data: { organisation: null } },
  };

export const paymentRequestNoDataError: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceWithInvoiceId,
            timezone: "Europe/London",
          },
        ],
        id: testOrganisation2._id,
        locale: "en-GB",
      },
    },
    result: { data: undefined },
  };

export const invalidOrAlteredPaymentRequestError: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: defaultReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      errors: [new Errors.UserInputError("Something has changed")],
    },
  };

export const paymentRequestError: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceWithInvoiceId,
            timezone: "Europe/London",
          },
        ],
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      errors: [new GraphQLError("Failed to load payment information")],
    },
  };

export const fullDiscountPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        discount: {
          customerEmail: userDetailsStripeInvoice.email,
          discountCode: "FREE_CODE",
        },
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: defaultOrganisation._id,
          payment: fullDiscountPayment,
        },
      },
    },
  };

export const partialDiscountPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        discount: {
          customerEmail: userDetailsStripeInvoice.email,
          discountCode: "TEST_CODE",
        },
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: defaultOrganisation._id,
          payment: discountPayment,
        },
      },
    },
  };

export const minChargeAdjustmentPaymentRequest: MockedResponse<GetOrganisationPaymentQuery> =
  {
    request: {
      query: GetOrganisationPaymentDocument,
      variables: {
        bookingReservations: [
          {
            reservedSpaceId: successReservedSpaceId,
            timezone: "Europe/London",
          },
        ],
        discount: {
          customerEmail: userDetailsStripeInvoice.email,
          discountCode: "MIN_CHARGE",
        },
        id: defaultOrganisation._id,
        locale: "en-GB",
      },
    },
    result: {
      data: {
        organisation: {
          __typename: "Organisation",
          _id: defaultOrganisation._id,
          payment: minChargeAdjustmentPayment,
        },
      },
    },
  };
